import { Link } from "wouter";
import { ArrowLeft, Shield } from "lucide-react";
import { Footer } from "@/components/website/Footer";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "data-collected", label: "Data We Collect" },
  { id: "how-we-use", label: "How We Use Your Data" },
  { id: "data-sharing", label: "Data Sharing" },
  { id: "payment-data", label: "Stripe & Payment Data" },
  { id: "data-retention", label: "Data Retention" },
  { id: "cookies", label: "Cookies" },
  { id: "your-rights", label: "Your Rights" },
  { id: "security", label: "Security" },
  { id: "international", label: "International Transfers" },
  { id: "children", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/95 border-b border-slate-200 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link href="/" className="flex items-center gap-2.5">
            <img src="/images/finproject-logo.png" alt="FinProject" className="h-8 w-auto" />
            <span className="font-bold text-slate-900 hidden sm:inline">FinProject</span>
          </Link>
          <Link href="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 flex gap-12">
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-28">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map(s => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block text-sm text-slate-500 hover:text-slate-900 py-1 transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <article className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          </div>
          <p className="text-sm text-slate-500 mb-10">
            Last updated: <strong>27 March 2026</strong> · Effective date: 27 March 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-12">

            <section id="overview">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Overview</h2>
              <p className="text-slate-600 leading-relaxed">
                Business Elite LLC ("FinProject", "we", "our", or "us"), a company registered in the Shams Free Zone, United Arab Emirates, operates the FinProject construction financial management platform available at <strong>finproject.io</strong> (the "Service").
              </p>
              <p className="text-slate-600 leading-relaxed mt-3">
                This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website or use our Service. Please read this policy carefully. If you disagree with its terms, please discontinue use of the Service.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="data-collected">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Data We Collect</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We collect information in the following categories:</p>

              <div className="space-y-5">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">Account & Identity Data</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 text-sm">
                    <li>Full name and email address</li>
                    <li>Company name, country, and industry</li>
                    <li>Role within your organisation</li>
                    <li>Password (stored as a bcrypt hash — we never store plaintext passwords)</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">Financial & Project Data</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 text-sm">
                    <li>Project details, budgets, BOQs, and financial records you enter into the platform</li>
                    <li>Invoice, payment, payroll, and cash flow data you create</li>
                    <li>Subcontractor and supplier information you add</li>
                    <li>Uploaded documents and file attachments</li>
                  </ul>
                  <p className="text-xs text-slate-400 mt-3">This data belongs to you. We process it solely to provide the Service.</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">Technical & Usage Data</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 text-sm">
                    <li>IP address, browser type, and operating system</li>
                    <li>Pages visited and features used (anonymised where possible)</li>
                    <li>Session identifiers stored in secure cookies</li>
                    <li>Error and crash reports via Sentry</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">Communication Data</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 text-sm">
                    <li>Emails or messages you send to our support team</li>
                    <li>Demo request details including phone number and country</li>
                  </ul>
                </div>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="how-we-use">
              <h2 className="text-xl font-bold text-slate-900 mb-4">How We Use Your Data</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 rounded-tl-lg">Purpose</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 rounded-tr-lg">Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ["Providing and maintaining the Service", "Contract performance"],
                      ["Creating and managing your account", "Contract performance"],
                      ["Processing subscription payments via Stripe", "Contract performance"],
                      ["Sending transactional emails (receipts, invitations, password resets)", "Contract performance"],
                      ["Responding to support requests", "Legitimate interest"],
                      ["Monitoring platform health and preventing abuse", "Legitimate interest"],
                      ["Improving features based on aggregated, anonymised usage data", "Legitimate interest"],
                      ["Complying with legal obligations (tax records, regulatory requests)", "Legal obligation"],
                      ["Sending product updates or offers (with your consent)", "Consent — opt-out any time"],
                    ].map(([purpose, basis], i) => (
                      <tr key={i} className="bg-white">
                        <td className="px-4 py-3 text-slate-600">{purpose}</td>
                        <td className="px-4 py-3 text-slate-500">{basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="data-sharing">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Data Sharing</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We do not sell your personal data. We share information only in the following limited circumstances:
              </p>
              <div className="space-y-4">
                {[
                  {
                    party: "Stripe, Inc.",
                    purpose: "Payment processing, subscription management, and fraud prevention. Stripe may collect payment card data and billing information directly.",
                    link: "https://stripe.com/privacy",
                  },
                  {
                    party: "Resend",
                    purpose: "Transactional email delivery (invitations, receipts, password resets). Email addresses are shared only for delivery purposes.",
                    link: "https://resend.com/legal/privacy-policy",
                  },
                  {
                    party: "Sentry",
                    purpose: "Application error monitoring. Crash reports may include technical context; we minimise PII in error payloads.",
                    link: "https://sentry.io/privacy/",
                  },
                ].map(({ party, purpose, link }) => (
                  <div key={party} className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-1">{party}</h3>
                        <p className="text-sm text-slate-600">{purpose}</p>
                      </div>
                      <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline shrink-0">Privacy policy ↗</a>
                    </div>
                  </div>
                ))}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-1">Legal & Regulatory</h3>
                  <p className="text-sm text-slate-600">We may disclose data when required by law, court order, or to protect the rights, property, or safety of FinProject, our users, or others.</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-1">Business Transfers</h3>
                  <p className="text-sm text-slate-600">In the event of a merger, acquisition, or sale of assets, your data may be transferred. We will notify you via email and/or a prominent notice on the Service.</p>
                </div>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="payment-data">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Stripe & Payment Data</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
                <p className="text-slate-700 leading-relaxed">
                  All payment card data is processed by <strong>Stripe, Inc.</strong> — a <strong>PCI DSS Level 1</strong> certified payment processor, the highest level of certification available in the payments industry. FinProject <strong>never</strong> stores, transmits, or has access to your full card number, CVV, or bank account details.
                </p>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">What Stripe Collects</h3>
                  <p className="text-slate-700 leading-relaxed text-sm">
                    When you subscribe to FinProject, Stripe collects and processes the following data directly:
                  </p>
                  <ul className="list-disc list-inside text-slate-700 space-y-1 text-sm mt-2">
                    <li>Payment card details (card number, expiry date, CVC)</li>
                    <li>Billing name and billing address</li>
                    <li>IP address and device information (for fraud prevention)</li>
                  </ul>
                  <p className="text-slate-600 text-sm mt-2">
                    This data is handled entirely by Stripe's infrastructure and is never transmitted to or stored on FinProject servers.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">What FinProject Retains</h3>
                  <p className="text-slate-700 leading-relaxed text-sm">
                    FinProject stores only the following identifiers to manage your subscription and link billing records to your account:
                  </p>
                  <ul className="list-disc list-inside text-slate-700 space-y-1 text-sm mt-2">
                    <li><strong>Stripe Customer ID</strong> — a unique identifier linking your FinProject account to your Stripe billing profile</li>
                    <li><strong>Stripe Subscription ID</strong> — a reference to your active subscription for plan management and invoicing</li>
                  </ul>
                  <p className="text-slate-600 text-sm mt-2">
                    We do not store card numbers, bank account details, or any other sensitive payment credentials.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">Stripe's Privacy Policy</h3>
                  <p className="text-slate-700 leading-relaxed text-sm">
                    Stripe's handling of your payment data is governed by their own privacy policy. You can review it at: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">stripe.com/privacy</a>.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">Payment Data Requests</h3>
                  <p className="text-slate-700 leading-relaxed text-sm">
                    If you wish to access, correct, or delete payment data held by Stripe, you may contact Stripe directly via their privacy centre at <a href="https://stripe.com/privacy-center/legal" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">stripe.com/privacy-center/legal</a>, or contact us at <a href="mailto:support@finproject.io" className="text-blue-700 underline">support@finproject.io</a> and we will assist in coordinating your request.
                  </p>
                </div>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="data-retention">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Data Retention</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We retain your data as follows:</p>
              <ul className="space-y-3">
                {[
                  ["Active account data", "Retained for the lifetime of your account"],
                  ["Financial records (invoices, payments, payroll)", "7 years from the end of the fiscal year in which they were created, to comply with UAE commercial and tax law"],
                  ["Session cookies", "Expire after 30 days of inactivity"],
                  ["Error logs (Sentry)", "90 days"],
                  ["Marketing communications consent records", "Until consent is withdrawn, plus 2 years"],
                  ["Data after account deletion", "Purged within 30 days, except where legal retention obligations apply"],
                ].map(([item, policy]) => (
                  <li key={item} className="flex gap-3 bg-white rounded-lg border border-slate-200 p-4">
                    <span className="font-medium text-slate-800 w-56 shrink-0 text-sm">{item}</span>
                    <span className="text-slate-600 text-sm">{policy}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-slate-200" />

            <section id="cookies">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Cookies</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We use the following cookies:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Cookie</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ["connect.sid", "Essential", "Authentication session — keeps you logged in securely"],
                      ["Sentry session replay", "Functional", "Anonymous error context for debugging — no keystroke recording"],
                    ].map(([name, type, purpose]) => (
                      <tr key={name} className="bg-white">
                        <td className="px-4 py-3 font-mono text-slate-700 text-xs">{name}</td>
                        <td className="px-4 py-3 text-slate-500">{type}</td>
                        <td className="px-4 py-3 text-slate-600">{purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                We do not use advertising or tracking cookies. You can block cookies via your browser settings; note that disabling the session cookie will prevent login.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="your-rights">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Your Rights</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Depending on your jurisdiction, you may have the following rights regarding your personal data:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["Access", "Request a copy of your personal data."],
                  ["Rectification", "Correct inaccurate or incomplete information."],
                  ["Erasure", "Request deletion of your account and data (subject to legal retention obligations)."],
                  ["Portability", "Export your financial data in CSV or PDF format from within the app."],
                  ["Restriction", "Ask us to pause processing of your data in certain circumstances."],
                  ["Objection", "Object to processing based on legitimate interest, including marketing."],
                  ["Withdraw Consent", "Opt out of marketing emails at any time via the unsubscribe link or your account settings."],
                  ["Lodge a Complaint", "File a complaint with your local data protection authority."],
                ].map(([right, desc]) => (
                  <div key={right} className="bg-white rounded-lg border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-800 text-sm mb-1">{right}</h3>
                    <p className="text-sm text-slate-600">{desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed mt-5">
                To exercise any of these rights, contact us at <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a>. We will respond within 30 days.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="security">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Security</h2>
              <p className="text-slate-600 leading-relaxed">
                We implement industry-standard security measures including TLS encryption in transit, password hashing (bcrypt), server-side session management with PostgreSQL-backed session storage, and role-based access controls. However, no system is completely secure. In the event of a data breach that affects your rights and freedoms, we will notify you as required by applicable law.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="international">
              <h2 className="text-xl font-bold text-slate-900 mb-4">International Transfers</h2>
              <p className="text-slate-600 leading-relaxed">
                FinProject is operated from the United Arab Emirates. Your data may be processed by our sub-processors in the United States (Stripe, Sentry, Resend). Where required, such transfers rely on Standard Contractual Clauses or equivalent safeguards recognised by applicable data protection law.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="children">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed">
                FinProject is a business-to-business platform intended for use by adults in a professional capacity. We do not knowingly collect personal information from persons under 18 years of age. If you believe we have inadvertently collected such data, please contact us immediately.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="changes">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material changes by email to your registered address and by posting a notice on the Service at least 14 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated policy.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="contact">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Contact Us</h2>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <p className="text-slate-700 font-semibold mb-4">Data Controller</p>
                <div className="text-slate-600 space-y-1 text-sm">
                  <p><strong>Business Elite LLC</strong></p>
                  <p>Shams Free Zone, Sharjah, United Arab Emirates</p>
                  <p>
                    Email: <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a>
                  </p>
                </div>
              </div>
            </section>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
}
